import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared in-memory cache across instances
const CACHE = new Map();
const MAX_CACHE = 150;
const cacheGet = (k) => CACHE.get(k);
const cacheSet = (k, v) => {
  if (CACHE.size >= MAX_CACHE) CACHE.delete(CACHE.keys().next().value);
  CACHE.set(k, v);
};

const inputCls =
  'w-full h-12 pl-10 pr-9 rounded-xl bg-[#0B1220]/80 border text-white placeholder:text-zinc-500 text-sm outline-none transition-all duration-200 focus:ring-2';

/**
 * Dark-themed address autocomplete for the public Book Online form.
 * Hits the public (no-auth) geocode endpoints. On selection, returns the full
 * parsed address (street/city/county/state/zip + lat/lng) and marks it verified.
 */
export default function PublicAddressAutocomplete({ value, onChange, error }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const search = async (q) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 3) { setResults([]); setLoading(false); return; }
    const key = trimmed.toLowerCase();
    const cached = cacheGet(key);
    if (cached) { setResults(cached); setOpen(cached.length > 0); setLoading(false); return; }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/geocode/public/autocomplete?q=${encodeURIComponent(trimmed)}`);
      if (reqId !== reqIdRef.current) return;
      const data = res.data?.results || [];
      cacheSet(key, data);
      setResults(data);
      setOpen(data.length > 0);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setResults([]); setOpen(false);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setVerified(false);
    onChange({ address: val, verified: false });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 3) {
      const key = val.trim().toLowerCase();
      if (cacheGet(key)) { search(val); return; }
      setLoading(true);
    } else { setLoading(false); }
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = async (item) => {
    const addr = item.address || {};
    const typedMatch = (query || '').trim().match(/^(\d+[A-Za-z]?)\s+/);
    const typedNumber = typedMatch ? typedMatch[1] : '';
    const houseNumber = typedNumber || addr.house_number || '';
    const streetParts = [houseNumber, addr.road].filter(Boolean);
    const streetAddress = streetParts.length > 0
      ? streetParts.join(' ')
      : (item.display_name?.split(',').slice(0, 2).join(',').trim() || '');

    let cityVal = addr.city || addr.town || addr.village || addr.hamlet ||
      addr.municipality || addr.city_district || addr.suburb || addr.locality || '';
    let countyVal = (addr.county || '').replace(' County', '');
    const zipVal = addr.postcode || '';
    const stateVal = addr.state || '';

    if (!countyVal && zipVal) {
      try {
        const r = await axios.get(`${API_URL}/geocode/public/county-by-zip?zip_code=${zipVal}`);
        if (r.data?.success && r.data.county) countyVal = r.data.county;
      } catch { /* silent */ }
    }
    if (!cityVal && zipVal) {
      try {
        const r = await axios.get(`${API_URL}/geocode/public/city-by-zip?zip_code=${zipVal}`);
        if (r.data?.success && r.data.city) cityVal = r.data.city;
      } catch { /* silent */ }
    }

    setQuery(streetAddress);
    setOpen(false);
    setVerified(true);
    onChange({
      address: streetAddress,
      city: cityVal,
      county: countyVal,
      state: stateVal || 'FL',
      zip_code: zipVal,
      lat: item.lat,
      lng: item.lon,
      verified: true,
    });
  };

  const borderState = error
    ? 'border-red-500/70 focus:border-red-500 focus:ring-red-500/25'
    : verified
      ? 'border-emerald-500/60 focus:border-emerald-500 focus:ring-emerald-500/25'
      : 'border-white/[0.1] focus:border-[#1FB6E9] focus:ring-[#1FB6E9]/25';

  return (
    <div ref={wrapperRef} className="relative" data-testid="public-address-autocomplete">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#38BDF8]" />
        <input
          data-testid="book-address"
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Start typing your street address…"
          className={`${inputCls} ${borderState}`}
          autoComplete="off"
        />
        {loading
          ? <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 animate-spin" />
          : verified
            ? <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
            : null}
      </div>
      {open && results.length > 0 && (
        <div data-testid="address-suggestions" className="absolute z-50 w-full mt-1.5 bg-[#0B1220] rounded-xl shadow-2xl border border-white/[0.12] max-h-64 overflow-y-auto">
          {results.map((item, i) => (
            <button
              key={i}
              type="button"
              data-testid={`address-option-${i}`}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-3.5 py-2.5 hover:bg-[#1FB6E9]/10 transition-colors border-b border-white/[0.05] last:border-0"
            >
              <p className="text-sm text-zinc-200 truncate">{item.display_name}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">
                {item.address?.county && `${item.address.county} · `}
                {item.address?.postcode || ''}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
